package dev.lingvo.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ProjectSummaryDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.shared.atLeast
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.components.automation.openFirstQaError
import dev.lingvo.site.components.automation.showJobToast
import dev.lingvo.site.components.editor.editorLocaleOptions
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LModalMotionPolicy
import dev.lingvo.site.ds.LModalSize
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.ToastState
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.nav.AppNav
import dev.lingvo.site.nav.AppRoute
import dev.lingvo.site.nav.NavGlyph
import dev.lingvo.site.components.automation.PretranslateConfirm
import dev.lingvo.site.state.ProjectDirectory
import dev.lingvo.site.state.WorkspaceSelection
import dev.lingvo.site.state.liveProjectRoute
import kotlinx.browser.document
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

/**
 * Whether the palette is open, and what it should already have in its field.
 *
 * A global rather than a `remember`, because two places on opposite sides of `AuthGuard`'s
 * `content()` call need to reach it: the top bar's search field (deep inside `content()`, only
 * mounted on `AppShell` routes) asks for it, and [CommandPaletteHost] (mounted after `content()`, so
 * it reaches the editor too) owns it. The same shape [dev.lingvo.site.state.WorkspaceSelection] and
 * [ProjectDirectory] already use for state more than one caller needs.
 *
 * [seed] is what makes the shell's search field *be* the palette rather than merely open it: the
 * character somebody typed into the bar arrives here and is already in the palette's own field, so
 * the keystroke is not lost and nothing has to be typed twice.
 */
object CommandPaletteState {
    var open: Boolean by mutableStateOf(false)
        private set

    var seed: String by mutableStateOf("")
        private set

    /** Refused while another modal — the editor's shortcut sheet, a confirm dialog — owns the screen. */
    fun request(seed: String = "") {
        if (!open && document.querySelector("[role=\"dialog\"]") == null) {
            this.seed = seed
            open = true
        }
    }

    fun close() {
        open = false
        seed = ""
    }
}

/** A pre-translate the palette asked for, held until the confirm has finished with it. */
internal data class PendingPretranslate(
    val slug: String,
    val projectName: String,
    val locales: List<String>,
)

/**
 * Mounts the `⌘K` / `Ctrl K` listener and renders the palette, its confirm, and its fallback toasts.
 *
 * Lives in `AuthGuard`, not `AppShell` — `EditorLayout.kt` renders `AuthGuard` directly and never
 * calls `AppShell` (D3 keeps the editor's shell-less layout), so a listener installed only inside
 * the shell would be silently dead on the one route somebody has open for eight hours. `AuthGuard`
 * is the one composable both `AppLayout` and `EditorLayout` already call, and it is the correct
 * place regardless: the palette needs a session to draw anything.
 *
 * **Outcomes go to the shell's toast host**, published by `AppShell` through [ShellToastHost]. This
 * composable sits outside the shell's `LocalToasts` provider, so it cannot read the local — and an
 * action row that spends credits with nowhere to report the receipt is worse than no action row. On
 * the editor route, which has no shell, the palette carries a host of its own rather than swallowing
 * the message.
 *
 * The confirm is mounted *here* rather than inside the palette's modal, so closing the palette on
 * activate does not tear down the dialog the activation just opened.
 */
@Composable
fun CommandPaletteHost(currentPath: String) {
    DisposableEffect(Unit) {
        val onKeyDown: (Event) -> Unit = { event ->
            val keyboardEvent = event as? KeyboardEvent
            if (keyboardEvent != null &&
                (keyboardEvent.metaKey || keyboardEvent.ctrlKey) &&
                // `code`, not `key` — the same defect Task 1 fixed in the editor. `Ctrl+K`'s `key`
                // is layout-dependent for the small set of layouts where that physical key is not
                // "k"; `code` names the physical key regardless.
                keyboardEvent.code == "KeyK" &&
                // **Not on top of a dialog.** An open modal is exactly when a surface refuses its
                // own shortcuts — the editor's resolver returns null for everything but Escape
                // while one is up (`EditorShortcuts.kt:69`) — and a palette appearing over a
                // confirm dialog is the same class of defect as the palette silently eating the
                // editor's Copy source chord: a global listener that does not ask what it is
                // interrupting.
                document.querySelector("[role='dialog']") == null
            ) {
                keyboardEvent.preventDefault()
                CommandPaletteState.request()
            }
        }
        document.addEventListener("keydown", onKeyDown)
        onDispose { document.removeEventListener("keydown", onKeyDown) }
    }

    val ctx = rememberPageContext()
    val fallbackToasts = rememberToasts()
    val shellToasts = ShellToastHost.state
    val toasts = shellToasts ?: fallbackToasts
    var pending by remember { mutableStateOf<PendingPretranslate?>(null) }

    CommandPalette(
        open = CommandPaletteState.open,
        onClose = CommandPaletteState::close,
        currentPath = currentPath,
        toasts = toasts,
        onPretranslate = { pending = it },
    )
    pending?.let { request ->
        PretranslateConfirm(
            open = true,
            slug = request.slug,
            locales = request.locales,
            onStarted = { jobId ->
                showJobToast(
                    toasts = toasts,
                    router = ctx.router,
                    projectSlug = request.slug,
                    jobId = jobId,
                    message = tr(
                        "command_palette.pre_translation_started",
                        "Pre-translation started.",
                    ),
                )
            },
            onClose = { pending = null },
        )
    }
    // Only where nothing else is listening. Two hosts on one page would draw every message twice.
    if (shellToasts == null) ToastHost(fallbackToasts)
}

/**
 * The `⌘K` surface.
 *
 * Built on [LModal] rather than a new overlay, so focus trapping, `Escape` and scrim behaviour come
 * from the component that already gets them right — this only adds a query field, a ranked list, and
 * the rows that run rather than navigate.
 */
@Composable
internal fun CommandPalette(
    open: Boolean,
    onClose: () -> Unit,
    currentPath: String,
    /**
     * Where an action row reports its outcome. Defaulted to a host of its own so a fixture that
     * mounts the palette to check something else — the editor's occlusion tests do — does not have
     * to know about the shell's toast channel; production always passes [ShellToastHost]'s.
     */
    toasts: ToastState = rememberToasts(),
    onPretranslate: (PendingPretranslate) -> Unit = {},
) {
    LModal(
        open = open,
        title = t("command_palette.command_palette", "Command palette"),
        onClose = onClose,
        size = LModalSize.MD,
        motionPolicy = LModalMotionPolicy.INSTANT,
    ) {
        // Seeded from whatever was typed into the shell's search field, so the field *is* the
        // palette rather than a door to it. Re-keyed on the seed so a second open with a second
        // seed starts from the new one rather than from the last query.
        var query by remember(CommandPaletteState.seed) { mutableStateOf(CommandPaletteState.seed) }
        // **The keyboard lands in the field, because the field is the whole surface.**
        //
        // `LModal` focuses the first focusable descendant it can find, and its own header draws the
        // "Close dialog" button before the content — so the palette opened with the keyboard on
        // Close: the letters somebody typed went nowhere, Space or Enter shut the palette they had
        // just asked for, and a screen reader announced a dialog whose only control was a close
        // button. The seed carried in from the shell's search bar was equally unreachable without a
        // blind Tab.
        //
        // Retried rather than assumed, the shape `LConfirmDialog` already uses for its
        // type-to-confirm field: the modal's own focus effect runs in the same frame as this one,
        // so the field has to be claimed after it rather than beside it.
        LaunchedEffect(open) {
            if (!open) return@LaunchedEffect
            repeat(PALETTE_FOCUS_ATTEMPTS) {
                delay(PALETTE_FOCUS_SETTLE_MS)
                val field = document.querySelector("#$COMMAND_PALETTE_ID input:not(:disabled)") as? HTMLElement
                if (field != null) {
                    field.focus()
                    if (document.activeElement == field) return@LaunchedEffect
                }
            }
        }
        val items = commandPaletteItems(
            currentPath = currentPath,
            query = query,
            toasts = toasts,
            onPretranslate = onPretranslate,
        )
        val grouped = remember(items, query) { groupCommands(rankCommands(items, query)) }

        // One container for the field and the rows, not two: `ArrowDown` from the field has to
        // reach the first row, and `moveRowFocus` (shared with `NavPicker`'s own popovers) finds
        // its rows by walking up from whichever element is focused to the nearest
        // `data-picker-popover` ancestor.
        Div(
            Modifier.fillMaxWidth().toAttrs {
                id(COMMAND_PALETTE_ID)
                attr(ROW_CONTAINER_ATTR, "")
                onKeyDown { event ->
                    val fromField = (event.target as? HTMLElement)?.tagName?.equals("input", ignoreCase = true) == true
                    when {
                        // `moveRowFocus` assumes the focused element is already a row — true for
                        // every existing caller, where the first row is focused programmatically
                        // before any key can land. Here the first keystroke can come from the
                        // field itself, which is not a row, so it needs its own bridge into the
                        // list rather than reusing that assumption incorrectly.
                        fromField && event.key == "ArrowDown" -> {
                            event.preventDefault()
                            firstCommandRow()?.focus()
                        }
                        fromField && event.key == "Enter" -> {
                            event.preventDefault()
                            firstCommandRow()?.click()
                        }
                        else -> moveRowFocus(event)
                    }
                }
            },
        ) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                // **A search field, which names itself.** The plain text field renders an
                // accessible name only from a visible `label`, and this call passed none — so the
                // palette's one input announced as an unnamed edit box, with nothing for a
                // voice-control user to say to reach it. `LSearchField` always writes `aria-label`,
                // and it is also the control the shell's top bar draws: typing there and landing
                // here is now one field continued rather than two that look different.
                //
                // `shortcutHint = null`: the bar's "/" cap belongs to the bar. Inside the palette
                // the key does nothing, and a cap that lies is worse than no cap.
                LSearchField(
                    value = query,
                    onValueChange = { query = it },
                    modifier = Modifier.fillMaxWidth(),
                    label = t(
                        "command_palette.search_commands_and_destinations",
                        "Search commands and destinations",
                    ),
                    placeholder = t(
                        "command_palette.jump_to_a_page_a_project",
                        "Jump to a page, a project, a language…",
                    ),
                    shortcutHint = null,
                )
                if (grouped.isEmpty()) {
                    // Announced, because it is the one result a reader cannot see: the list they
                    // were arrowing through is simply gone, and without a live region typing a
                    // query that matches nothing produces total silence.
                    Span(
                        LVisuallyHiddenStyle.toModifier().toAttrs {
                            attr("role", "status")
                            attr("aria-atomic", "true")
                        },
                    ) {
                        Text(tf("command_palette.no_matches_status", "No matches for {query}.", "query" to query))
                    }
                    PickerNote(tf("command_palette.no_matches_note", "No matches for “{query}”.", "query" to query))
                } else {
                    Column(Modifier.fillMaxWidth().gap(Space.SM)) {
                        grouped.forEach { (kind, rows) ->
                            Column(Modifier.fillMaxWidth().gap(2.px)) {
                                CommandSectionHeading(kind)
                                rows.forEach { item ->
                                    CommandRow(
                                        item = item,
                                        current = item.path == currentPath,
                                        onActivate = onClose,
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * The heading above one section of rows.
 *
 * A heading rather than a bare rule: "Actions" and "Keys" are the two sections whose rows do
 * different things from every other row in the list, and a divider alone says only "these are not
 * those" without saying which is which.
 */
@Composable
private fun CommandSectionHeading(kind: CommandKind) {
    val palette = ColorMode.current.toSitePalette()
    Span(
        Modifier
            .padding(topBottom = Space.XS, leftRight = Space.SM)
            .fontSize(TypeScale.Micro)
            .fontWeight(600)
            .letterSpacing(TypeScale.MicroTracking)
            .color(palette.textMuted)
            .styleModifier { property("text-transform", "uppercase") }
            .toAttrs { attr("data-command-section", kind.name.lowercase()) },
    ) {
        Text(
            when (kind) {
                CommandKind.ACTION -> t("command_palette.actions", "Actions")
                CommandKind.KEY -> t("command_palette.keys", "Keys")
                CommandKind.PROJECT -> t("command_palette.projects", "Projects")
                CommandKind.LOCALE -> t("command_palette.languages", "Languages")
                CommandKind.DESTINATION -> t("command_palette.go_to", "Go to")
            },
        )
    }
}

@Composable
internal fun CommandRow(
    item: CommandItem,
    current: Boolean,
    onActivate: () -> Unit,
    /**
     * Where a project row finds the mark it should be drawing.
     *
     * A lookup rather than a field on [CommandItem]: the row model is a value compared for equality
     * by `rankCommands`/`groupCommands`, and an icon belongs to the project rather than to the
     * query that surfaced it.
     */
    findProject: (String?) -> ProjectSummaryDto? = ProjectDirectory::find,
) {
    when (item.kind) {
        // The only kind that runs rather than navigates — see `CommandItem.path`'s KDoc.
        CommandKind.ACTION -> CommandActionRow(item, onActivate)
        // **A project is drawn as itself.** Eight projects used to carry eight identical folder
        // glyphs, in the one list a reader scans fastest, while every other surface in the app —
        // the rail, the breadcrumb, the workspace picker, the overview header — identifies them by
        // their own mark. `PickerRow` already draws that mark from a project's icon and accent, so
        // this is the lookup it needs and not a second drawing of one. The folder stands in only
        // while the directory has not answered for this slug.
        CommandKind.PROJECT -> {
            val href = item.path ?: return
            val summary = findProject(AppRoute.projectSlug(href))
            PickerRow(
                label = item.label,
                href = href,
                current = current,
                onActivate = onActivate,
                note = item.hint,
                iconKey = summary?.iconKey,
                accentKey = summary?.accentKey,
                slug = summary?.slug,
                hasCustomIcon = summary?.hasCustomIcon ?: false,
                glyph = if (summary != null) null else item.glyph?.let { glyph -> { NavIcon(glyph) } },
            )
        }
        CommandKind.DESTINATION, CommandKind.LOCALE, CommandKind.KEY -> {
            val href = item.path ?: return
            PickerRow(
                label = item.label,
                href = href,
                current = current,
                onActivate = onActivate,
                note = item.hint,
                glyph = item.glyph?.let { glyph -> { NavIcon(glyph) } },
            )
        }
    }
}

@Composable
internal fun CommandActionRow(item: CommandItem, onActivate: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Button(
        AppTrailRowStyle.toModifier().toAttrs {
            type(ButtonType.Button)
            attr(ROW_ATTR, "")
            attr("data-command-action", item.id)
            onClick {
                // Closed first: the palette is a launcher, and an action that opens a dialog or
                // routes has to do it against a screen the palette is no longer covering.
                onActivate()
                item.onRun?.invoke()
            }
        },
    ) {
        Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
            // The tick column PickerRow reserves, held here too so an action row's label starts on
            // the same x as every link row's.
            Span(Modifier.width(IconSize.MD).minWidth(IconSize.MD).toAttrs { attr("aria-hidden", "true") }) {}
            Span(
                Modifier
                    .iconSize(IconSize.MD)
                    .minWidth(IconSize.MD)
                    .color(palette.textSecondary)
                    .toAttrs { attr("aria-hidden", "true") },
            ) {
                item.glyph?.let { NavIcon(it) }
            }
            Span(Modifier.flexGrow(1).toAttrs { attr("dir", "auto") }) { Text(item.label) }
            item.hint?.let {
                Span(Modifier.fontSize(TypeScale.Small).color(palette.textMuted).toAttrs()) { Text(it) }
            }
        }
    }
}

/**
 * Every row the palette can show, before ranking.
 *
 * Built from data the shell already holds rather than a second copy of any of it: [AppNav.railFor]
 * for the destinations resident wherever this route is, [ProjectDirectory.projects] for every
 * project, [editorLocaleOptions] for the open project's languages, [AppNav.settingsFor] for the
 * workspace pages Stage 3 moved out of the rail, and [AppNav.paletteOnly] for the doors no rail
 * carries at all. That is the "reachable, not resident" decision the brief settled.
 *
 * **The actions are the point of this wave.** A palette of links is a keyboard shortcut for the
 * sidebar; the rows that spend credits, open the failing string, and move between workspaces are the
 * ones somebody opens `⌘K` for. Every one of them is gated on the authority that would actually be
 * checked — `canSpendCredits` comes from the server for exactly the reason spelled out on
 * `EditorCapabilitiesDto`, and a row that is not allowed is absent rather than present-and-refusing.
 */
@Composable
private fun commandPaletteItems(
    currentPath: String,
    query: String,
    toasts: ToastState,
    onPretranslate: (PendingPretranslate) -> Unit,
): List<CommandItem> {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val workspace = WorkspaceSelection.current()
    val role: Role? = workspace?.role
    val slug = AppRoute.projectSlug(currentPath)
    // `AppTrail` is what keeps this loaded everywhere else in the shell, and the editor's layout
    // never renders it (D3) — so the palette has to ask for itself, or its project list is silently
    // empty on the one route open eight hours a day.
    ProjectDirectory.ensure(scope, workspace?.slug)
    val summary = ProjectDirectory.find(slug)
    // The verified project the route gate already resolved, not a fresh request: it carries the
    // per-locale progress the pre-translate row counts from and the server's own capability answer.
    val detail = slug?.let { liveProjectRoute(it)?.project }
    val projectName = detail?.name ?: summary?.name.orEmpty()
    val firstTarget = summary?.locales?.firstOrNull { it != summary.sourceLocale }
        ?: detail?.locales?.firstOrNull { it != detail.sourceLocale }

    // **Labels translated here, keyed by id, the way the rail does it** (`AppShell.kt`). The nav
    // model's destinations are class-init `val`s evaluated before any catalogue is fetched, so
    // `destination.label` is English for the life of the page — every palette row sat in English
    // beside a rail that had already translated the same words.
    val destinations = AppNav.railFor(role, currentPath, firstTarget).destinations.map { destination ->
        CommandItem(
            id = "rail-${destination.id}",
            label = t("nav.${destination.catalogueKey}", destination.label),
            kind = CommandKind.DESTINATION,
            path = destination.path,
            glyph = destination.glyph,
        )
    }
    val projectHint = t("command_palette.project", "Project")
    val projects = ProjectDirectory.projects.map { project ->
        CommandItem(
            id = "project-${project.slug}",
            label = project.name,
            kind = CommandKind.PROJECT,
            path = "/app/projects/${project.slug}",
            hint = projectHint,
            glyph = NavGlyph.Folder,
        )
    }
    val localeHint = t("command_palette.language", "Language")
    val locales = if (summary != null) {
        editorLocaleOptions(
            slug = summary.slug,
            sourceLocale = summary.sourceLocale,
            locales = summary.locales,
            // Irrelevant here: the row's own "is this the current path" check decides the tick, not
            // this function's — this call only wants the label/path resolution it already does.
            current = "",
        ).map { option ->
            CommandItem(
                id = "locale-${summary.slug}-${option.code}",
                label = option.label,
                kind = CommandKind.LOCALE,
                path = option.path,
                hint = localeHint,
                glyph = NavGlyph.Languages,
            )
        }
    } else {
        emptyList()
    }
    val settingsHint = t("command_palette.settings", "Settings")
    val settings = AppNav.settingsFor(role).map { destination ->
        CommandItem(
            id = "settings-${destination.id}",
            label = t("nav.${destination.catalogueKey}", destination.label),
            kind = CommandKind.DESTINATION,
            path = destination.path,
            hint = settingsHint,
            glyph = destination.glyph,
        )
    }
    // **The doors no rail carries.** `/app/start` teaches the CLI and its only other inbound links
    // are inside the dashboard's first-run block — which disappears the moment a project exists, on
    // a page a translator cannot open at all. Read from the model rather than minted here, so
    // `ReachableRoutesTest` counts them as real doors and the next such page is one line there.
    val setupHint = t("command_palette.setup", "Setup")
    val paletteOnly = AppNav.paletteOnly.map { destination ->
        CommandItem(
            id = "palette-${destination.id}",
            label = t("nav.${destination.catalogueKey}", destination.label),
            kind = CommandKind.DESTINATION,
            path = destination.path,
            hint = setupHint,
            glyph = destination.glyph,
        )
    }

    val currentWorkspaceSlug = workspace?.slug
    val actions = commandActions(
        slug = slug,
        projectName = projectName,
        detail = detail,
        role = role,
        firstTarget = firstTarget,
        otherWorkspaces = Session.organizations.filter { it.slug != currentWorkspaceSlug },
        onPretranslate = onPretranslate,
        onFixFirstQaError = { projectSlug ->
            scope.launch { openFirstQaError(ctx.router, projectSlug, locale = null, toasts = toasts) }
        },
        onNavigate = { ctx.router.tryRoutingTo(it) },
        onSwitchWorkspace = { other ->
            WorkspaceSelection.select(other.slug)
            ctx.router.tryRoutingTo(AppNav.landingFor(other.role))
        },
    )
    val keys = commandKeyRows(slug, query)

    // Keys are already the server's answer to this query, so they are appended after ranking rather
    // than filtered again by label: a key whose *source text* matched would otherwise be thrown away
    // because its name does not contain what was typed.
    return actions + destinations + projects + locales + settings + paletteOnly + keys
}

/**
 * The rows that do something.
 *
 * **Pure, and that is what makes the gating checkable.** Every row here is absent unless the exact
 * authority behind it is held, and "absent, not disabled" is a claim a test has to be able to make
 * about a *list* — not about pixels in a shell that needs an authenticated session, a routed page
 * and six stubbed endpoints before it will draw anything at all. Every input arrives as an argument
 * and every effect leaves as a callback, so `CommandPaletteActionsBrowserTest` can ask what a
 * translator is offered without pretending to be one.
 *
 * `tr`/`trf` rather than `t`/`tf` because this is not composition. The catalogue is fixed before the
 * first composition either way, so the sentences are the same ones.
 *
 * @param detail the verified project, when the route has one. `canSpendCredits` is read from it and
 *   never inferred from [role]: role ≥ ADMIN is only half the server's test, the credential's
 *   `mt:spend` scope is the other half, and it is invisible from here.
 * @param otherWorkspaces the memberships that are not the one being rendered.
 */
internal fun commandActions(
    slug: String?,
    projectName: String,
    detail: ProjectDetailDto?,
    role: Role?,
    firstTarget: String?,
    otherWorkspaces: List<WorkspaceDto>,
    onPretranslate: (PendingPretranslate) -> Unit,
    onFixFirstQaError: (String) -> Unit,
    onNavigate: (String) -> Unit,
    onSwitchWorkspace: (WorkspaceDto) -> Unit,
): List<CommandItem> {
    val actions = mutableListOf<CommandItem>()
    val isAdmin = role?.atLeast(Role.ADMIN) == true

    // **Pre-translate, with the number in the label.** The locale is the one furthest behind, and
    // the count is what is actually empty there — "Translate all" is the label this rule exists to
    // forbid.
    if (slug != null && detail != null && detail.editorCapabilities?.canSpendCredits == true && isAdmin) {
        val worst = detail.progress
            .filter { it.locale != detail.sourceLocale }
            .maxByOrNull { row -> (row.total - row.translated).coerceAtLeast(0) }
        val untranslated = worst?.let { (it.total - it.translated).coerceAtLeast(0) } ?: 0
        if (worst != null && untranslated > 0) {
            actions += CommandItem(
                id = "pretranslate-$slug-${worst.locale}",
                label = trf(
                    "command_palette.pre_translate_count_untranslated_in_language",
                    "Pre-translate {count} untranslated in {language} · {project}",
                    "count" to untranslated,
                    "language" to localeInfo(worst.locale).englishName,
                    "project" to projectName,
                ),
                kind = CommandKind.ACTION,
                path = null,
                glyph = NavGlyph.Bot,
                onRun = { onPretranslate(PendingPretranslate(slug, projectName, listOf(worst.locale))) },
            )
        }
    }

    if (slug != null) {
        actions += CommandItem(
            id = "fix-first-qa-$slug",
            label = trf(
                "command_palette.fix_the_first_qa_error_in",
                "Fix the first QA error in {project}",
                "project" to projectName,
            ),
            kind = CommandKind.ACTION,
            path = null,
            glyph = NavGlyph.Check,
            onRun = { onFixFirstQaError(slug) },
        )
        firstTarget?.let { locale ->
            actions += CommandItem(
                id = "review-queue-$slug-$locale",
                label = trf(
                    "command_palette.open_the_review_queue_for_language",
                    "Open the review queue for {language}",
                    "language" to localeInfo(locale).englishName,
                ),
                kind = CommandKind.ACTION,
                path = null,
                glyph = NavGlyph.SquareCheck,
                onRun = { onNavigate("/app/projects/$slug/review/$locale") },
            )
        }
        if (isAdmin) {
            actions += CommandItem(
                id = "import-strings-$slug",
                label = trf(
                    "command_palette.import_strings_into_project",
                    "Import strings into {project}",
                    "project" to projectName,
                ),
                kind = CommandKind.ACTION,
                path = null,
                glyph = NavGlyph.ArrowUpDown,
                onRun = { onNavigate("/app/projects/$slug/data") },
            )
        }
    }

    if (isAdmin) {
        actions += CommandItem(
            id = "new-project",
            label = tr("command_palette.new_project", "New project"),
            kind = CommandKind.ACTION,
            path = null,
            glyph = NavGlyph.Folder,
            onRun = { onNavigate("/app/projects/new") },
        )
        actions += CommandItem(
            id = "invite-member",
            label = tr("command_palette.invite_a_member", "Invite a member"),
            kind = CommandKind.ACTION,
            path = null,
            glyph = NavGlyph.Users,
            onRun = { onNavigate("/app/settings/members") },
        )
    }

    // Switching is an action, not a link: the selection is stored state that every other tab shares,
    // so a row that merely navigated would leave the shell rendering a workspace nobody chose.
    otherWorkspaces.forEach { other ->
        actions += CommandItem(
            id = "switch-workspace-${other.slug}",
            label = trf(
                "command_palette.switch_to_workspace_name",
                "Switch to {name}",
                "name" to other.name,
            ),
            kind = CommandKind.ACTION,
            path = null,
            glyph = NavGlyph.Box,
            onRun = { onSwitchWorkspace(other) },
        )
    }
    return actions
}

/**
 * Keys from the open project, for the query as typed.
 *
 * **Debounced, and only from two characters up.** The palette recomposes on every keystroke, so an
 * undebounced request would fire one search per character on the heaviest endpoint in the product —
 * and a one-character search matches nearly every key in a project, which is a long list nobody
 * asked for. Eight rows, because the palette is a launcher and the keys page is the list.
 */
@Composable
private fun commandKeyRows(slug: String?, query: String): List<CommandItem> {
    var settled by remember(slug) { mutableStateOf("") }
    val typed = query.trim()
    LaunchedEffect(slug, typed) {
        if (slug == null || typed.length < 2) {
            settled = ""
        } else {
            delay(COMMAND_KEY_DEBOUNCE_MS)
            settled = typed
        }
    }
    val hint = t("command_palette.key", "Key")
    val rows = loadAsync(slug, settled) {
        if (slug == null || settled.isBlank()) {
            emptyList()
        } else {
            ApiClient.listKeys(projectSlug = slug, search = settled, limit = COMMAND_KEY_ROWS).items
        }
    }
    return (rows as? Async.Success)?.value.orEmpty().map { row ->
        CommandItem(
            id = "key-${row.key.id}",
            label = row.key.name,
            kind = CommandKind.KEY,
            path = "/app/projects/$slug/keys?search=${row.key.name}",
            hint = hint,
            glyph = NavGlyph.Key,
        )
    }
}

private fun firstCommandRow(): HTMLElement? =
    document.getElementById(COMMAND_PALETTE_ID)?.querySelector("[$ROW_ATTR]") as? HTMLElement

/** The one command palette in the app — singleton, so a plain id is safe. */
private const val COMMAND_PALETTE_ID = "lingvo-command-palette"

/** How many frames the query field is willing to wait for the modal to finish claiming focus. */
private const val PALETTE_FOCUS_ATTEMPTS = 3

/** One frame, roughly, between attempts. */
private const val PALETTE_FOCUS_SETTLE_MS = 10L

/** Long enough that a typist never fires a search mid-word, short enough to feel immediate. */
private const val COMMAND_KEY_DEBOUNCE_MS = 220L

/** The palette is a launcher, not the keys page. */
private const val COMMAND_KEY_ROWS = 8
